import { useCallback, useEffect, useRef, useState } from 'react';

import ConsentCard from './ConsentCard';
import DoNotContactModal from './DoNotContactModal';
import EnrolModal from './EnrolModal';
import JourneyCard from './JourneyCard';
import PauseJourneysModal from './PauseJourneysModal';
import TimelineList from './TimelineList';
import { Banner, ErrorText, Row } from './ui';
import useMessagingTimeline from './useMessagingTimeline';
import useStepLabels from './useStepLabels';
import { formatDay } from '../../Lifecycle/shared/format';
import SectionState from '../../Lifecycle/shared/SectionState';

import Button from '@/components/Button';
import useAuth from '@/hooks/useAuth';
import {
  WorkerLifecycleOverview,
  lifecycleAPI,
  lifecycleErrorMessage,
} from '@/services/lifecycleAPI';
import {
  LifecycleAction,
  lifecycleFailureOutcome,
  trackLifecycleActionTaken,
  trackLifecycleFeatureUsed,
} from '@/util/vexo';

type Props = { workerId: number | string };

type Dialog = 'pause' | 'dnc' | 'enrol' | null;

// graphy names the failed rule ("Cannot enrol: journey_does_not_fit"); only
// that rule name is sent, never the free text of another error.
const enrolRule = (err: unknown): string | null =>
  /Cannot enrol: ([a-z_]+)/.exec(lifecycleErrorMessage(err, ''))?.[1] ?? null;

const MessagingPanel = ({ workerId }: Props) => {
  const { currentAdminIsTenantAdminRole } = useAuth();
  const timeline = useMessagingTimeline(workerId);
  const stepLabel = useStepLabels();
  const { reload } = timeline;
  const [overview, setOverview] = useState<WorkerLifecycleOverview | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const latest = useRef(0);

  const load = useCallback(() => {
    const request = ++latest.current;
    setLoading(true);
    setError(null);
    lifecycleAPI
      .getWorkerOverview(workerId)
      .then((result) => {
        if (request !== latest.current) return;
        setOverview(result);
        setLoading(false);
      })
      .catch((err) => {
        if (request !== latest.current) return;
        setError(lifecycleErrorMessage(err, 'Could not load messaging.'));
        setLoading(false);
      });
  }, [workerId]);

  useEffect(load, [load]);

  // A refused write ("already paused", "not paused", a 409) means someone
  // else changed the worker: show what is true now, without the loading
  // state that would close the open dialog (review D-08).
  const refresh = useCallback(() => {
    const request = ++latest.current;
    lifecycleAPI
      .getWorkerOverview(workerId)
      .then((result) => {
        if (request === latest.current) setOverview(result);
      })
      .catch(() => undefined);
  }, [workerId]);

  useEffect(() => {
    trackLifecycleFeatureUsed({
      feature: 'worker_messaging_open',
      surface: 'worker_profile',
      worker_id: workerId,
    });
  }, [workerId]);

  const apply = async (
    action: LifecycleAction,
    write: () => Promise<WorkerLifecycleOverview>,
    journeyKey: string | null = null,
  ) => {
    const event = {
      action,
      surface: 'worker_profile' as const,
      worker_id: workerId,
      journey_key: journeyKey,
    };
    let fresh: WorkerLifecycleOverview;
    try {
      fresh = await write();
    } catch (err) {
      trackLifecycleActionTaken({
        ...event,
        outcome: lifecycleFailureOutcome(err),
        error: action === 'worker_enrol' ? enrolRule(err) : null,
      });
      refresh();
      throw err;
    }
    trackLifecycleActionTaken({ ...event, outcome: 'success' });
    latest.current += 1;
    setOverview(fresh);
    setDialog(null);
    reload();
  };

  const applyInline = async (
    action: LifecycleAction,
    write: () => Promise<WorkerLifecycleOverview>,
    failure: string,
  ) => {
    setBusy(true);
    setActionError(null);
    try {
      await apply(action, write);
    } catch (err) {
      setActionError(lifecycleErrorMessage(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const prefs = overview?.preferences;
  const paused = Boolean(prefs?.journeys_paused_at);
  const doNotContact =
    Boolean(prefs?.do_not_contact_at) ||
    overview?.suppressed_reason === 'do_not_contact';
  const holdout = overview?.journeys.find(
    (journey) => journey.status === 'active' && journey.holdout,
  );

  return (
    <div
      data-testid="workerMessagingPanel"
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      {loading && !overview && (
        <SectionState mode="loading" title="messaging" />
      )}
      {!loading && error && !overview && (
        <SectionState
          error={error}
          mode="error"
          title="messaging"
          onRetry={load}
        />
      )}
      {overview && prefs && (
        <>
          {actionError && (
            <div role="alert">
              <ErrorText>{actionError}</ErrorText>
            </div>
          )}
          {paused && (
            <Banner role="status" tone="amber">
              <strong>
                {`Journeys paused${prefs.journeys_paused_by ? ` by ${prefs.journeys_paused_by}` : ''} on ${formatDay(prefs.journeys_paused_at)}${prefs.journeys_pause_reason ? `: ${prefs.journeys_pause_reason}` : ''}.`}
              </strong>
              <span>Shift messages still go.</span>
              <Button
                a11yLabel="Resume journeys"
                css={{ marginLeft: 'auto' }}
                data-testid="resumeJourneysBtn"
                disabled={busy}
                label="Resume"
                onClick={() =>
                  applyInline(
                    'worker_resume',
                    () => lifecycleAPI.resumeJourneys(workerId),
                    'Could not resume journeys.',
                  )
                }
              />
            </Banner>
          )}
          {doNotContact && (
            <Banner role="status" tone="red">
              <strong>
                {`Do not contact since ${formatDay(prefs.do_not_contact_at)}${prefs.do_not_contact_reason ? `: ${prefs.do_not_contact_reason}` : ''}.`}
              </strong>
              <span>No lifecycle messages of any kind.</span>
              <Button
                a11yLabel="Remove do not contact"
                css={{ marginLeft: 'auto' }}
                data-testid="removeDoNotContactBtn"
                disabled={busy}
                label="Remove"
                onClick={() =>
                  applyInline(
                    'worker_dnc_off',
                    () =>
                      lifecycleAPI.setDoNotContact(workerId, {
                        do_not_contact: false,
                      }),
                    'Could not remove do not contact.',
                  )
                }
              />
            </Banner>
          )}
          {holdout && (
            <Banner tone="grey">
              {`This worker is in the holdout group for ${holdout.journey_key}, so they get no ${holdout.journey_key} messages by design.`}
            </Banner>
          )}
          <Row css={{ gap: '16px', alignItems: 'stretch' }}>
            <ConsentCard
              doNotContact={doNotContact}
              paused={paused}
              preferences={prefs}
              onDoNotContact={() => setDialog('dnc')}
              onPause={() => setDialog('pause')}
            />
            <JourneyCard
              canEnrol={currentAdminIsTenantAdminRole}
              overview={overview}
              stepLabel={stepLabel}
              onEnrol={() => setDialog('enrol')}
            />
          </Row>
        </>
      )}
      <TimelineList
        channel={timeline.channel}
        journey={timeline.journey}
        error={timeline.error}
        items={timeline.items}
        stepLabel={stepLabel}
        kind={timeline.kind}
        loading={timeline.loading}
        page={timeline.page}
        pagination={timeline.pagination}
        onChannel={timeline.setChannel}
        onJourney={timeline.setJourney}
        onKind={timeline.setKind}
        onPage={timeline.setPage}
        onRetry={timeline.retry}
      />
      {dialog === 'pause' && (
        <PauseJourneysModal
          onClose={() => setDialog(null)}
          onSubmit={(reason) =>
            apply('worker_pause', () =>
              lifecycleAPI.pauseJourneys(workerId, reason),
            )
          }
        />
      )}
      {dialog === 'dnc' && (
        <DoNotContactModal
          onClose={() => setDialog(null)}
          onSubmit={(reason) =>
            apply('worker_dnc_on', () =>
              lifecycleAPI.setDoNotContact(workerId, {
                do_not_contact: true,
                reason,
              }),
            )
          }
        />
      )}
      {dialog === 'enrol' && overview && (
        <EnrolModal
          journeys={overview.journeys}
          onClose={() => setDialog(null)}
          onSubmit={(key) =>
            apply(
              'worker_enrol',
              () => lifecycleAPI.enrolJourney(workerId, key),
              key,
            )
          }
        />
      )}
    </div>
  );
};

export default MessagingPanel;
